import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type {
  PlatformAdapter,
  PlatformContext as PlatformCtx,
  UserInfo,
} from '@annoteer/platform-adapter';
import { TeamsPlatformAdapter } from '@annoteer/platform-adapter/TeamsPlatformAdapter';

interface PlatformContextValue {
  adapter: PlatformAdapter;
  context: PlatformCtx | null;
  user: UserInfo | null;
  isInitialized: boolean;
  error: string | null;
}

/**
 * Dev/preview override: `?clientPreview=mobile` forces the mobile client
 * treatment, which suppresses every purchase and payment surface (Microsoft
 * certification policy 114048).
 *
 * There is no other way to see that UI short of a real phone. The client type
 * comes from the Teams HOST, not the browser, so Chrome device emulation and
 * Teams web responsive mode both leave it untouched and give a false pass.
 * Same convention as `?billingPreview=` and `?showLightMeetingsBanner=1`.
 *
 * Deliberately one-way: it can only force 'mobile', never away from it.
 * An override that could force 'desktop' would be able to reveal a price
 * inside a real Teams mobile app — the exact violation this guards against.
 */
function applyClientPreview(ctx: PlatformCtx): PlatformCtx {
  if (typeof window === 'undefined') return ctx;
  const params = new URLSearchParams(window.location.search);
  return params.get('clientPreview') === 'mobile' ? { ...ctx, clientType: 'mobile' } : ctx;
}

/**
 * ?surfacePreview=stage renders the meeting stage outside Teams, where no host
 * exists to set frameContext -- how the e2e suite reaches the stage at all.
 * Applied ONLY on the dev fallback path: inside Teams, frameContext is Teams'
 * to decide.
 */
function devFrameContext(): 'meetingStage' | 'sidePanel' {
  if (typeof window === 'undefined') return 'sidePanel';
  return new URLSearchParams(window.location.search).get('surfacePreview') === 'stage'
    ? 'meetingStage'
    : 'sidePanel';
}

const PlatformContext = createContext<PlatformContextValue | null>(null);

export function PlatformProvider({ children }: { children: ReactNode }) {
  const [adapter] = useState(() => new TeamsPlatformAdapter());
  const [context, setContext] = useState<PlatformCtx | null>(null);
  const [user, setUser] = useState<UserInfo | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function init() {
      try {
        await adapter.initialize();
        const ctx = await adapter.getContext();
        const currentUser = await adapter.getCurrentUser();
        setContext(applyClientPreview(ctx));
        setUser(currentUser);
        setIsInitialized(true);
      } catch (err) {
        console.warn('Teams SDK init failed, running in dev mode:', err);
        // Dev mode fallback when not in Teams
        setContext(
          applyClientPreview({
            frameContext: devFrameContext(),
            // Not in Teams at all, so no host client to speak of — and no
            // mobile policy to honour. See mapClientType in the adapter.
            clientType: 'unknown',
            theme: 'default',
          }),
        );
        setUser({
          id: 'dev-user',
          displayName: 'Dev User',
        });
        setIsInitialized(true);
        setError('Not running in Teams — using dev mode');
      }
    }
    init();
  }, [adapter]);

  return (
    <PlatformContext.Provider value={{ adapter, context, user, isInitialized, error }}>
      {children}
    </PlatformContext.Provider>
  );
}

export function usePlatform(): PlatformContextValue {
  const ctx = useContext(PlatformContext);
  if (!ctx) {
    throw new Error('usePlatform must be used within PlatformProvider');
  }
  return ctx;
}
